import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { Plus, Trash2, Calendar, MapPin, Pencil, ArrowDownAZ, ArrowUpAZ, Copy } from 'lucide-react'
import { useBandContext } from '@/contexts/BandContext'
import { useSetlists } from '@/hooks/useSetlists'
import { supabase } from '@/lib/supabase'
import { Button } from '@/components/ui/button'
import { Card, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog'

export const SetlistsPage = () => {
  const { selectedBandId } = useBandContext()
  const navigate = useNavigate()
  const { setlists, createSetlist, deleteSetlist, copySetlist } = useSetlists(selectedBandId || undefined)

  const [sortAsc, setSortAsc] = useState(false)
  const [showCreate, setShowCreate] = useState(false)
  const [name, setName] = useState('')
  const [venue, setVenue] = useState('')
  const [date, setDate] = useState(new Date().toISOString().split('T')[0])

  const handleCopy = async (id: string) => {
    try {
      await copySetlist(id)
      toast.success('Setlist copied')
    } catch {
      toast.error('Failed to copy setlist')
    }
  }

  const handleCreate = async () => {
    if (!name.trim() || !selectedBandId) return
    try {
      const setlist = await createSetlist({ name: name.trim(), venue: venue.trim(), date })
      // Auto-create a default section
      await supabase.from('setlist_sections').insert({ setlist_id: setlist.id, name: 'Set 1', position: 0 })
      setShowCreate(false)
      setName('')
      setVenue('')
      toast.success('Setlist created')
      navigate(`/setlists/${setlist.id}`)
    } catch {
      toast.error('Failed to create setlist')
    }
  }

  return (
    <div className='mx-auto max-w-4xl p-6 space-y-6'>
      <div className='flex items-center justify-between'>
        <h1 className='text-2xl font-bold'>Setlists</h1>
        {selectedBandId && (
          <Button size='sm' variant='outline' onClick={() => setShowCreate(true)}>
            <Plus className='mr-1 h-3 w-3' /> New Setlist
          </Button>
        )}
      </div>

      {selectedBandId && setlists.length > 1 && (
        <div className='flex gap-1'>
          <Button size='sm' variant={sortAsc ? 'outline' : 'default'} onClick={() => setSortAsc(false)}>
            <ArrowDownAZ className='mr-1 h-3 w-3' /> Newest
          </Button>
          <Button size='sm' variant={sortAsc ? 'default' : 'outline'} onClick={() => setSortAsc(true)}>
            <ArrowUpAZ className='mr-1 h-3 w-3' /> Oldest
          </Button>
        </div>
      )}

      {selectedBandId && [...setlists].sort((a, b) => sortAsc ? a.date.localeCompare(b.date) : b.date.localeCompare(a.date)).map((sl) => (
        <Card key={sl.id} className='cursor-pointer hover:bg-accent/50 transition-colors' onClick={() => navigate(`/setlists/${sl.id}`)}>
          <CardHeader className='flex flex-row items-center justify-between'>
            <div>
              <CardTitle className='text-lg'>{sl.name}</CardTitle>
              <div className='text-sm text-muted-foreground mt-1 space-y-0.5'>
                <div className='flex items-center gap-1'><MapPin className='h-3 w-3' /> {sl.venue || 'No venue'}</div>
                <div className='flex items-center gap-1'><Calendar className='h-3 w-3' /> {sl.date}</div>
              </div>
            </div>
            <div className='flex gap-1'>
              <Button variant='ghost' size='icon' onClick={(e) => { e.stopPropagation(); navigate(`/setlists/${sl.id}`) }}>
                <Pencil className='h-4 w-4' />
              </Button>
              <Button variant='ghost' size='icon' onClick={(e) => { e.stopPropagation(); handleCopy(sl.id) }}>
                <Copy className='h-4 w-4' />
              </Button>
              <Button variant='ghost' size='icon' onClick={(e) => { e.stopPropagation(); deleteSetlist(sl.id) }}>
                <Trash2 className='h-4 w-4' />
              </Button>
            </div>
          </CardHeader>
        </Card>
      ))}

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New Setlist</DialogTitle>
            <DialogDescription>Create a setlist for your next gig.</DialogDescription>
          </DialogHeader>
          <div className='space-y-3'>
            <div className='space-y-1'>
              <Label>Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder='e.g. Saturday Night at The Venue' />
            </div>
            <div className='space-y-1'>
              <Label>Venue</Label>
              <Input value={venue} onChange={(e) => setVenue(e.target.value)} placeholder='Venue name' />
            </div>
            <div className='space-y-1'>
              <Label>Date</Label>
              <Input type='date' value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant='outline' onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={!name.trim()}>Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
